import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlatformModal } from './PlatformModal.js';

/**
 * Reason-only operator mutation modal (suspend / restore / cancel). Composes
 * PlatformModal with a required reason textarea validated 1–500 chars (mirrors
 * the C-2 server Zod). On submit, calls onConfirm(reason); the parent owns the
 * mutation, success-close, and query invalidation.
 */
const ReasonSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Reason is required')
    .max(500, 'Reason must be 500 characters or fewer'),
});
type ReasonFormValues = z.infer<typeof ReasonSchema>;

export function PlatformReasonModal({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  confirmVariant = 'primary',
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (reason: string) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReasonFormValues>({ resolver: zodResolver(ReasonSchema) });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const confirmClass =
    confirmVariant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700'
      : 'bg-indigo-primary text-white hover:opacity-90';

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="platform-reason-form"
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-md font-medium disabled:opacity-50 ${confirmClass}`}
          >
            {isSubmitting ? 'Submitting…' : confirmLabel}
          </button>
        </>
      }
    >
      <form id="platform-reason-form" onSubmit={handleSubmit((v) => onConfirm(v.reason))}>
        {description ? <p className="text-sm text-gray-700 mb-3">{description}</p> : null}
        <label htmlFor="platform-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Reason
        </label>
        <textarea
          id="platform-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={4}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
